import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, Database, Lock, UserCheck, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="pt-32 pb-20 px-4 md:px-8 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <Shield className="w-16 h-16 text-purple-600 mx-auto mb-6" />
          <h1 className="text-5xl md:text-7xl font-bold font-heading text-slate-900 mb-6">
            Privacy Policy
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Your privacy matters. Learn how we collect, use, and protect your personal information.
          </p>
          <p className="text-sm text-slate-500 mt-4">Effective Date: December 2025 | NDPR Compliant</p>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 text-center">
              <Lock className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h3 className="font-bold text-slate-900 mb-2">Bank-Level Security</h3>
              <p className="text-sm text-slate-600">256-bit encryption protects all your data</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 text-center">
              <UserCheck className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="font-bold text-slate-900 mb-2">NDPR Compliant</h3>
              <p className="text-sm text-slate-600">Fully compliant with Nigerian data protection laws</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 text-center">
              <Globe className="w-12 h-12 text-purple-600 mx-auto mb-4" />
              <h3 className="font-bold text-slate-900 mb-2">GDPR Ready</h3>
              <p className="text-sm text-slate-600">International privacy standards met</p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">1. Information We Collect</h2>
            
            <div className="bg-slate-50 rounded-2xl p-6 mb-8">
              <h3 className="font-bold text-slate-900 mb-4">Personal Information</h3>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-2">
                  <Database className="w-5 h-5 text-brand-blue flex-shrink-0 mt-1" />
                  <span><strong>Identity Data:</strong> Full name, BVN, NIN, photo ID</span>
                </li>
                <li className="flex items-start gap-2">
                  <Database className="w-5 h-5 text-brand-blue flex-shrink-0 mt-1" />
                  <span><strong>Contact Data:</strong> Email, phone number, residential address</span>
                </li>
                {/* <li className="flex items-start gap-2">
                  <Database className="w-5 h-5 text-brand-blue flex-shrink-0 mt-1" />
                  <span><strong>Financial Data:</strong> Bank account details, transaction history, credit score</span>
                </li>
                <li className="flex items-start gap-2">
                  <Database className="w-5 h-5 text-brand-blue flex-shrink-0 mt-1" />
                  <span><strong>Device Data:</strong> IP address, device ID, browser type, location data</span>
                </li> */}
                {/* <li className="flex items-start gap-2">
                  <Database className="w-5 h-5 text-brand-blue flex-shrink-0 mt-1" />
                  <span><strong>Usage Data:</strong> App activity, features used, preferences</span>
                </li> */}
              </ul>
            </div>

            <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">2. How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">We use your personal data to:</p>
            <ul className="space-y-2 text-slate-700 mb-8">
              <li>• Provide and improve our financial services</li>
              <li>• Process transactions and verify your identity (KYC)</li>
              <li>• Prevent fraud and ensure account security</li>
              <li>• Comply with legal and regulatory requirements</li>
              <li>• Send you transaction notifications and updates</li>
              <li>• Provide customer support and resolve issues</li>
              <li>• Conduct credit assessments for loans</li>
              <li>• Personalize your app experience</li>
            </ul>

            <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">3. How We Share Your Information</h2>
            <div className="bg-indigo-50 rounded-2xl p-6 mb-8 border border-blue-100">
              <p className="text-slate-700 mb-4">
                <strong>We DO NOT sell your personal information.</strong> We only share your data with:
              </p>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start gap-2">
                  <Eye className="w-5 h-5 text-brand-blue flex-shrink-0 mt-1" />
                  <div>
                    <strong>Banking Partners:</strong> For transaction processing and fund transfers
                  </div>
                </li>
                {/* <li className="flex items-start gap-2">
                  <Eye className="w-5 h-5 text-brand-blue flex-shrink-0 mt-1" />
                  <div>
                    <strong>Credit Bureaus:</strong> For credit reporting and verification (with your consent)
                  </div>
                </li> */}
                <li className="flex items-start gap-2">
                  <Eye className="w-5 h-5 text-brand-blue flex-shrink-0 mt-1" />
                  <div>
                    <strong>Regulatory Authorities:</strong> CBN, NDIC, and law enforcement when required by law
                  </div>
                </li>
                {/* <li className="flex items-start gap-2">
                  <Eye className="w-5 h-5 text-brand-blue flex-shrink-0 mt-1" />
                  <div>
                    <strong>Service Providers:</strong> Cloud storage, analytics, and customer support (under strict NDAs)
                  </div>
                </li> */}
              </ul>
            </div>

            <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">4. Data Security Measures</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <Lock className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="font-bold text-slate-900 mb-2">Encryption</h3>
                <p className="text-sm text-slate-600">All data encrypted with AES-256 in transit (TLS 1.3) and at rest</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <Shield className="w-10 h-10 text-blue-600 mb-4" />
                <h3 className="font-bold text-slate-900 mb-2">Access Control</h3>
                <p className="text-sm text-slate-600">Role-based access with multi-factor authentication for staff</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <Database className="w-10 h-10 text-purple-600 mb-4" />
                <h3 className="font-bold text-slate-900 mb-2">Secure Storage</h3>
                <p className="text-sm text-slate-600">Data hosted in CBN-compliant Nigerian data centers</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <Eye className="w-10 h-10 text-orange-600 mb-4" />
                <h3 className="font-bold text-slate-900 mb-2">Monitoring</h3>
                <p className="text-sm text-slate-600">24/7 security monitoring and intrusion detection</p>
              </div>
            </div>

            <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">5. Your Privacy Rights (NDPR)</h2>
            <p className="text-slate-600 mb-4">Under the Nigeria Data Protection Regulation, you have the right to:</p>
            <div className="bg-slate-50 rounded-2xl p-6 mb-8">
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-sm font-bold">✓</span>
                  <div>
                    <strong>Access:</strong> Request a copy of your personal data we hold
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-sm font-bold">✓</span>
                  <div>
                    <strong>Rectification:</strong> Correct inaccurate or incomplete data
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-sm font-bold">✓</span>
                  <div>
                    <strong>Erasure:</strong> Request deletion after account closure (subject to retention requirements)
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-sm font-bold">✓</span>
                  <div>
                    <strong>Objection:</strong> Object to data processing for marketing purposes
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-sm font-bold">✓</span>
                  <div>
                    <strong>Portability:</strong> Receive your data in a structured format
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-sm font-bold">✓</span>
                  <div>
                    <strong>Withdraw Consent:</strong> Withdraw consent at any time (may limit services)
                  </div>
                </li>
              </ul>
            </div>

            <p className="text-slate-600 mb-8">
              To exercise your rights, contact our Data Protection Officer at <a href="mailto:dpo@britedge.ng" className="text-brand-blue hover:underline">dpo@britedge.ng</a>
            </p>

            <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">6. Cookies and Tracking</h2>
            <p className="text-slate-600 mb-4">
              We use cookies and similar technologies to enhance your experience:
            </p>
            <ul className="space-y-2 text-slate-700 mb-8">
              <li><strong>Essential Cookies:</strong> Required for login and security</li>
              <li><strong>Analytics Cookies:</strong> Help us understand app usage and improve features</li>
              <li><strong>Marketing Cookies:</strong> Personalize content and ads (with your consent)</li>
            </ul>
            <p className="text-slate-600 mb-8">
              You can manage cookie preferences in your browser settings or app settings menu.
            </p>

            <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">7. Data Retention</h2>
            <p className="text-slate-600 mb-4">We retain your personal data for:</p>
            <ul className="space-y-2 text-slate-700 mb-8">
              <li>• <strong>Active accounts:</strong> Duration of relationship</li>
              <li>• <strong>After account closure:</strong> 7 years (CBN requirement)</li>
              <li>• <strong>Marketing data:</strong> Until you opt-out</li>
              <li>• <strong>Transaction records:</strong> 7 years (tax and audit purposes)</li>
            </ul>

            <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">8. Children's Privacy</h2>
            <p className="text-slate-600 mb-8">
              Britedge services are not intended for individuals under 18 years old. We do not knowingly collect personal information from children. If we become aware of such collection, we will delete the data immediately.
            </p>

            <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">9. Changes to This Policy</h2>
            <p className="text-slate-600 mb-8">
              We may update this Privacy Policy periodically. Material changes will be communicated via email or in-app notification 30 days before taking effect. Your continued use after changes indicates acceptance.
            </p>

            <div className="bg-white rounded-2xl p-8 border-2 border-brand-blue mt-12">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Contact Our Data Protection Officer</h3>
              <p className="text-slate-600 mb-4">
                For privacy concerns, questions, or to exercise your rights:
              </p>
              <div className="space-y-2 text-slate-700">
                <p><strong>Email:</strong> dpo@britedge.ng</p>
                <p><strong>Phone:</strong> +234 (0) 800 BRITEDGE</p>
                <p><strong>Address:</strong> Plot 123, Admiralty Way, Lekki Phase 1, Lagos, Nigeria</p>
                <p className="text-sm text-slate-500 mt-4">Response time: Within 30 days as required by NDPR</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 px-4 md:px-8 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="w-12 h-12 mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-4">Your Privacy is Our Priority</h3>
          <p className="text-slate-300 mb-6">
            Questions? Concerns? Our Data Protection Officer is here to help.
          </p>
          <Button onClick={() => navigate('/contact')} className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-4">
            Contact Us
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
